import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { involvedAPI, progressAPI } from '../../../services/apiServices';
import { useFeedback } from '../../../contexts/FeedbackContext';
import { Alert, Input, Modal, Select, Textarea } from '../../../components/common/UI';
import DateField from '../../../components/common/DateField';
import { CalendarIcon, EditIcon, PlusIcon, TrashIcon } from '../../../components/common/Icons';
import { PROGRESS_STATUS_KEYS } from '../../../utils/businessStatus';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import {
  currentTimeValue,
  formatLocalizedDateTime,
  toDateInputValue,
  todayInputValue,
} from '../../../utils/dateUtils';
import QuickContactModal from './QuickContactModal';

export const statusClass = (status) => {
  const classes = {
    pre_initial: 'bg-amber-50 text-amber-700',
    started: 'bg-amber-50 text-amber-700',
    in_progress: 'bg-emerald-50 text-emerald-700',
    paused: 'bg-yellow-50 text-yellow-700',
    awaiting_send: 'bg-sky-50 text-sky-700',
    awaiting_receive: 'bg-violet-50 text-violet-700',
    aborted: 'bg-rose-50 text-rose-700',
    canceled: 'bg-red-50 text-red-700',
    completed: 'bg-slate-100 text-slate-700',
    other: 'bg-slate-100 text-slate-700',
  };
  return classes[status] || classes.other;
};

const createEmptyForm = () => ({
  id: null,
  status: 'in_progress',
  description: '',
  launchDate: todayInputValue(),
  launchTime: currentTimeValue(),
  responsibleClientId: '',
  responsibleInvolvedId: '',
  responsibleName: '',
  files: [],
});

const formatFileSize = (size = 0) => {
  if (!size) return '';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const toLocalTime = (value) => {
  if (!value) return currentTimeValue();
  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getInvolvedName = (item) => item?.client?.name || item?.involvedName || '';
const getResponsibleName = (item) =>
  getInvolvedName(item.responsibleInvolved) || item.responsibleContact?.name || item.responsibleName || '';

const timeValue = (value) => {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const createdAtValue = (item) => timeValue(item?.createdAt || item?.updatedAt || item?.launchDate);

export const sortProgressRows = (items = [], now = new Date()) => {
  const nowTime = now.getTime();
  return [...items].sort((a, b) => {
    const futureA = timeValue(a?.launchDate) > nowTime;
    const futureB = timeValue(b?.launchDate) > nowTime;
    if (futureA && futureB) {
      const launchDiff = timeValue(a.launchDate) - timeValue(b.launchDate);
      return launchDiff || createdAtValue(b) - createdAtValue(a);
    }
    if (futureA !== futureB) return futureA ? -1 : 1;
    return createdAtValue(b) - createdAtValue(a);
  });
};

const CALENDAR_EVENT_MINUTES = 30;

const toGoogleCalendarDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

export const buildGoogleCalendarUrl = ({ businessName, item, t }) => {
  const startDate = new Date(item?.launchDate);
  if (Number.isNaN(startDate.getTime())) return '';
  const endDate = new Date(startDate.getTime() + CALENDAR_EVENT_MINUTES * 60 * 1000);
  const status = t(`status.${item?.status || 'other'}`, item?.status || 'other');
  const responsible = getResponsibleName(item);
  const details = [
    item?.description,
    `${t('common.status')}: ${status}`,
    responsible ? `${t('progress.responsibleName')}: ${responsible}` : '',
  ].filter(Boolean).join('\n');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Clarity Files - ${businessName || t('filters.business')}`,
    dates: `${toGoogleCalendarDate(startDate)}/${toGoogleCalendarDate(endDate)}`,
    details,
    location: 'Clarity Files',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const openCalendarWindow = (url) => {
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened) opened.opener = null;
  return Boolean(opened);
};

const ProgressTab = ({ businessId, businessName = '', data = [], involved = [], onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(createEmptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [error, setError] = useState('');
  const [quickContactOpen, setQuickContactOpen] = useState(false);
  const [createdInvolved, setCreatedInvolved] = useState([]);
  const [detail, setDetail] = useState(null);
  const [localRows, setLocalRows] = useState(data);
  const [blockedCalendarUrl, setBlockedCalendarUrl] = useState('');
  const { confirm, notify } = useFeedback();

  useEffect(() => {
    setLocalRows(data);
  }, [data]);

  const rows = useMemo(() => sortProgressRows(localRows), [localRows]);
  const locale = i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : 'pt-BR';
  const statusOptions = useMemo(
    () => PROGRESS_STATUS_KEYS.map((value) => ({ value, label: t(`status.${value}`) })),
    [t]
  );
  const involvedOptions = useMemo(
    () => [
      { value: '', label: t('progress.noResponsibleInvolved') },
      ...[...createdInvolved, ...involved].map((person) => ({ value: person.id, label: getInvolvedName(person) || t('involved.manualPerson') })),
    ],
    [createdInvolved, involved, t]
  );

  const openCreate = () => {
    setForm(createEmptyForm());
    setError('');
    setModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      status: item.status || 'in_progress',
      description: item.description || '',
      launchDate: toDateInputValue(item.launchDate),
      launchTime: toLocalTime(item.launchDate),
      responsibleClientId: item.responsibleClientId || item.responsibleContact?.id || '',
      responsibleInvolvedId: item.responsibleInvolvedId || item.responsibleInvolved?.id || '',
      responsibleName: item.responsibleName || '',
      files: [],
    });
    setError('');
    setModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = {
      status: form.status,
      description: form.description || null,
      responsibleClientId: null,
      responsibleInvolvedId: form.responsibleInvolvedId || null,
      responsibleName: form.responsibleName.trim() || null,
      ...(form.launchDate ? {
        launchDate: new Date(`${form.launchDate}T${form.launchTime || '09:00'}:00`).toISOString(),
      } : {}),
    };

    try {
      const response = form.id
        ? await progressAPI.update(businessId, form.id, payload)
        : await progressAPI.create(businessId, payload);
      const progressId = form.id || response?.data?.data?.id;
      if (progressId && form.files?.length) {
        setUploadingFiles(true);
        await Promise.all(form.files.map((file) => progressAPI.uploadAttachment(businessId, progressId, file)));
      }
      setModal(false);
      setForm(createEmptyForm());
      onRefresh();
    } catch (err) {
      setError(getApiErrorMessage(err, t('progress.saveError')));
    } finally {
      setUploadingFiles(false);
      setSaving(false);
    }
  };

  const downloadAttachment = async (progressId, attachment) => {
    try {
      const response = await progressAPI.downloadAttachment(businessId, progressId, attachment.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: attachment.mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      notify({ variant: 'danger', title: t('attachments.downloadErrorTitle'), message: t('attachments.downloadError') });
    }
  };

  const downloadCalendar = async (item) => {
    try {
      const response = await progressAPI.downloadCalendar(businessId, item.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/calendar' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `clarity-progress-${item.id}.ics`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      notify({ variant: 'danger', title: t('progress.calendarErrorTitle'), message: t('progress.calendarError') });
    }
  };

  const openGoogleCalendar = (item) => {
    const url = buildGoogleCalendarUrl({ businessName, item, t });
    if (!url) {
      notify({ variant: 'danger', title: t('progress.calendarErrorTitle'), message: t('progress.calendarError') });
      return;
    }
    if (openCalendarWindow(url)) {
      setBlockedCalendarUrl('');
      return;
    }
    setBlockedCalendarUrl(url);
    notify({ variant: 'warning', title: t('progress.calendarPopupBlockedTitle'), message: t('progress.calendarPopupBlocked') });
  };

  const removeAttachment = async (progressId, attachment) => {
    const ok = await confirm({
      variant: 'danger',
      title: t('attachments.deleteTitle'),
      message: t('attachments.deleteMessage', { name: attachment.originalName }),
      confirmText: t('common.delete'),
    });
    if (!ok) return;
    try {
      await progressAPI.removeAttachment(businessId, progressId, attachment.id);
      onRefresh();
    } catch {
      notify({ variant: 'danger', title: t('attachments.deleteErrorTitle'), message: t('attachments.deleteError') });
    }
  };

  const handleQuickContactCreated = async (contact) => {
    const { data: response } = await involvedAPI.create(businessId, {
      clientId: contact.id,
      involvedName: null,
      role: t('involved.defaultRole'),
    });
    const person = response.data || response;
    const hydratedPerson = {
      ...person,
      clientId: person.clientId || contact.id,
      client: person.client || contact,
    };
    setCreatedInvolved((current) => [hydratedPerson, ...current.filter((item) => item.id !== hydratedPerson.id)]);
    setForm((current) => ({ ...current, responsibleInvolvedId: hydratedPerson.id, responsibleName: '' }));
  };

  const deleteDescription = (item) => {
    const date = item.launchDate ? formatLocalizedDateTime(item.launchDate, locale) : t('common.date');
    const description = item.description || getResponsibleName(item) || t(`status.${item.status || 'other'}`, item.status || '');
    const trimmed = description.length > 80 ? `${description.slice(0, 80)}...` : description;
    return t('progress.deleteDetailMessage', {
      detail: `${t(`status.${item.status || 'other'}`, item.status || '')} - ${date} - ${trimmed}`,
    });
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      variant: 'danger',
      title: t('progress.deleteTitle'),
      message: deleteDescription(item),
      confirmText: t('common.delete'),
    });
    if (!ok) return;
    try {
      await progressAPI.remove(businessId, item.id);
      onRefresh();
    } catch {
      notify({ variant: 'danger', title: t('progress.deleteErrorTitle'), message: t('progress.deleteError') });
    }
  };

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
      <div className="flex min-h-16 items-center justify-between gap-4 border-b border-slate-200 px-4 py-3">
        <h2 className="font-bold text-slate-900">{t('reports.tabs.progress')}</h2>
        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sky-500 px-5 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600" onClick={openCreate} type="button">
          <PlusIcon className="h-4 w-4" />
          {t('common.new')}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-500">{t('progress.empty')}</p>
      ) : (
        <div className="divide-y divide-slate-200">
          {blockedCalendarUrl && (
            <div className="px-4 py-3">
              <Alert variant="warning" onClose={() => setBlockedCalendarUrl('')}>
                <a className="font-bold text-sky-700 underline" href={blockedCalendarUrl} target="_blank" rel="noreferrer">
                  {t('progress.openGoogleCalendar')}
                </a>
              </Alert>
            </div>
          )}
          {rows.map((item) => (
            <div key={item.id} className="grid cursor-pointer gap-3 px-4 py-4 transition hover:bg-slate-50 md:grid-cols-[1fr_auto_auto] md:items-start" onClick={() => setDetail(item)} role="button" tabIndex={0}>
              <div>
                <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                  <CalendarIcon className="h-4 w-4" />
                  {formatLocalizedDateTime(item.launchDate, i18n.language)}
                </div>
                <p className="text-slate-900">{item.description || t('common.description')}</p>
                {getResponsibleName(item) && (
                  <p className="mt-2 text-xs font-semibold text-slate-500">
                    {t('businessDetails.by', { name: getResponsibleName(item) })}
                  </p>
                )}
            {!!item.attachments?.length && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs font-bold text-slate-500">{t('attachments.attachedPrefix', 'Anexado:')}</span>
                {item.attachments.map((attachment) => (
                      <span key={attachment.id} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                        <button type="button" onClick={(event) => { event.stopPropagation(); downloadAttachment(item.id, attachment); }} className="hover:text-sky-600">
                          {attachment.originalName}
                        </button>
                        <button type="button" onClick={(event) => { event.stopPropagation(); removeAttachment(item.id, attachment); }} className="text-red-500" aria-label={t('attachments.delete')}>
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-bold ${statusClass(item.status)}`}>
                {t(`status.${item.status}`, item.status)}
              </span>
              <div className="flex justify-end gap-1">
                <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-sky-600 hover:bg-sky-50" onClick={(event) => { event.stopPropagation(); openGoogleCalendar(item); }} type="button" aria-label={t('progress.addToCalendar')}>
                  <CalendarIcon className="h-4 w-4" />
                </button>
                <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100" onClick={(event) => { event.stopPropagation(); openEdit(item); }} type="button" aria-label={t('progress.edit')}>
                  <EditIcon className="h-4 w-4" />
                </button>
                <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 hover:bg-red-50" onClick={(event) => { event.stopPropagation(); handleDelete(item); }} type="button" aria-label={t('progress.delete')}>
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modal} onClose={() => setModal(false)} title={form.id ? t('progress.edit') : t('progress.new')} size="lg">
        {error && <Alert variant="error" onClose={() => setError('')}>{error}</Alert>}
        <div className="mt-4 space-y-4">
          <Textarea label={t('common.description')} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder={t('progress.descriptionPlaceholder')} rows={4} />
          <div>
            <Select label={t('common.status')} value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })} options={statusOptions} />
            <span className={`mt-2 inline-flex w-fit rounded-full px-3 py-1 text-xs font-bold ${statusClass(form.status)}`}>
              {t(`status.${form.status}`, form.status)}
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
            <Select
              label={t('progress.responsibleInvolved')}
              value={form.responsibleInvolvedId}
              onChange={(event) => setForm({ ...form, responsibleInvolvedId: event.target.value })}
              options={involvedOptions}
            />
            <button className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 text-sm font-bold text-sky-700 hover:bg-sky-100" onClick={() => setQuickContactOpen(true)} type="button">
              <PlusIcon className="h-4 w-4" />
              {t('quickContact.shortAction')}
            </button>
          </div>
          <Input
            label={t('progress.responsibleName')}
            value={form.responsibleName}
            onChange={(event) => setForm({ ...form, responsibleName: event.target.value })}
            placeholder={t('progress.responsibleNamePlaceholder')}
            helpText={t('progress.responsibleHelp')}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <DateField
              label={t('common.date')}
              value={form.launchDate}
              onChange={(launchDate) => setForm({ ...form, launchDate })}
              helpText={t('common.dateHelp')}
            />
            <Input
              label={t('hours.time')}
              type="time"
              value={form.launchTime}
              onChange={(event) => setForm({ ...form, launchTime: event.target.value })}
            />
          </div>
          <label className="block rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
            <span className="block text-sm font-bold text-slate-900">{t('attachments.add')}</span>
            <span className="mt-1 block text-xs text-slate-500">{t('attachments.help')}</span>
            <input
              className="mt-3 block w-full text-sm text-slate-700"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={(event) => setForm({ ...form, files: Array.from(event.target.files || []) })}
            />
            {!!form.files?.length && (
              <div className="mt-3 space-y-2">
                <span className="block text-xs font-bold text-sky-700">
                  {uploadingFiles ? t('attachments.uploading', 'Enviando anexos...') : t('attachments.selected', { count: form.files.length })}
                </span>
                {form.files.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-xs text-slate-700">
                    <span className="min-w-0 truncate font-semibold">
                      {file.name} {formatFileSize(file.size) ? `- ${formatFileSize(file.size)}` : ''}
                    </span>
                    <button
                      type="button"
                      className="font-bold text-rose-500 disabled:opacity-50"
                      disabled={saving}
                      onClick={() => setForm((current) => ({
                        ...current,
                        files: current.files.filter((_, fileIndex) => fileIndex !== index),
                      }))}
                    >
                      {uploadingFiles ? t('attachments.uploadingShort', 'Enviando') : t('common.remove', 'Remover')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </label>
          <div className="flex gap-2">
            <button className="inline-flex h-10 items-center justify-center rounded-lg bg-sky-500 px-5 text-sm font-bold text-white disabled:opacity-60" onClick={handleSave} disabled={saving} type="button">
              {saving ? t('common.saving') : t('common.save')}
            </button>
            <button className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900" onClick={() => setModal(false)} type="button">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </Modal>
      <QuickContactModal isOpen={quickContactOpen} onClose={() => setQuickContactOpen(false)} onCreated={handleQuickContactCreated} />
      <Modal isOpen={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.description || t('progress.new')} size="sm">
        {detail && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Info label={t('common.status')} value={t(`status.${detail.status}`, detail.status)} />
            <Info label={t('progress.dateTime')} value={detail.launchDate ? formatLocalizedDateTime(detail.launchDate, locale) : '-'} />
            <Info label={t('progress.responsibleName')} value={getResponsibleName(detail) || '-'} />
            <Info className="sm:col-span-2" label={t('common.description')} value={detail.description || '-'} />
            {!!detail.attachments?.length && (
              <Info className="sm:col-span-2" label={t('attachments.attachedPrefix', 'Anexado:')} value={detail.attachments.map((attachment) => attachment.originalName).join(', ')} />
            )}
            <button
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 text-sm font-bold text-sky-700 hover:bg-sky-100 sm:col-span-2"
              onClick={() => openGoogleCalendar(detail)}
              type="button"
            >
              <CalendarIcon className="h-4 w-4" />
              {t('progress.addToCalendar')}
            </button>
            <button
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-xs font-bold text-slate-600 hover:bg-slate-50 sm:col-span-2"
              onClick={() => downloadCalendar(detail)}
              type="button"
            >
              {t('progress.downloadCalendarFile')}
            </button>
          </div>
        )}
      </Modal>
    </section>
  );
};

const Info = ({ label, value, className = '' }) => (
  <div className={`rounded-lg bg-slate-50 p-3 ${className}`}>
    <p className="text-xs font-bold text-slate-500">{label}</p>
    <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

export default ProgressTab;
